/**
 * extractFromSegmentJoins — token-aware splitter for a single comma-separated
 * FROM segment. Walks the segment range and partitions it into a leading FROM
 * item plus an ordered list of JOIN strips. Each strip carries its kind
 * keyword run, optional `LATERAL` modifier, RHS text, and either an `ON`
 * predicate range or a `USING (...)` group. Pure, structural — uses
 * `tokenSkip` for string / comment / quoted-identifier skipping (VQE-RO9).
 */

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import { consumeParenGroup } from '../insert/insertParseHelpers';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

export type JoinKind =
  | 'INNER'
  | 'LEFT'
  | 'RIGHT'
  | 'FULL'
  | 'CROSS'
  | 'CROSS_APPLY'
  | 'OUTER_APPLY';

export interface JoinSpan {
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

export interface JoinClause {
  readonly kind: JoinKind;
  readonly isLateral: boolean;
  readonly kindStart: number;
  readonly kindEnd: number;
  readonly rhs: JoinSpan;
  readonly on: JoinSpan | null;
  readonly using: JoinSpan | null;
  readonly start: number;
  readonly end: number;
}

export interface FromSegmentSplit {
  readonly leading: JoinSpan;
  readonly joins: readonly JoinClause[];
}

interface KeywordRun {
  readonly kindStart: number;
  readonly kindEnd: number;
  readonly kind: JoinKind;
  readonly isLateral: boolean;
}

interface PeekedWord {
  readonly upper: string;
  readonly start: number;
  readonly end: number;
}

export function extractFromSegmentJoins(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): FromSegmentSplit {
  const ctx = skipContextFor(options);
  const runs = findJoinKeywordRuns(source, start, end, ctx);
  if (runs.length === 0) {
    return { leading: makeSpan(source, start, end), joins: [] };
  }
  const leading = makeSpan(source, start, runs[0].kindStart);
  const joins: JoinClause[] = [];
  for (let r = 0; r < runs.length; r++) {
    const run = runs[r];
    const stripEnd = r + 1 < runs.length ? runs[r + 1].kindStart : end;
    const inner = scanInnerStrip(source, run.kindEnd, stripEnd, ctx);
    joins.push({
      kind: run.kind,
      isLateral: run.isLateral,
      kindStart: run.kindStart,
      kindEnd: run.kindEnd,
      rhs: makeSpan(source, run.kindEnd, inner.rhsEnd),
      on: inner.on,
      using: inner.using,
      start: run.kindStart,
      end: stripEnd,
    });
  }
  return { leading, joins };
}

function findJoinKeywordRuns(
  source: string,
  start: number,
  end: number,
  ctx: SkipContext,
): KeywordRun[] {
  const runs: KeywordRun[] = [];
  let i = start;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren > 0) { i++; continue; }
    if (/\s/.test(c)) { i++; continue; }
    if (!IDENT_START.test(c)) { i++; continue; }
    const wStart = i;
    i++;
    while (i < end && IDENT_CONT.test(source[i])) i++;
    const word = source.slice(wStart, i).toUpperCase();
    const matched = matchJoinRun(word, source, i, end, ctx);
    if (matched) {
      let after = matched.endIndex;
      let isLateral = false;
      const peek = peekWord(source, after, end);
      if (peek && peek.upper === 'LATERAL') {
        after = peek.end;
        isLateral = true;
      }
      runs.push({ kindStart: wStart, kindEnd: after, kind: matched.kind, isLateral });
      i = after;
    }
  }
  return runs;
}

function matchJoinRun(
  firstWord: string,
  source: string,
  after: number,
  end: number,
  ctx: SkipContext,
): { kind: JoinKind; endIndex: number } | null {
  switch (firstWord) {
    case 'JOIN':
      return { kind: 'INNER', endIndex: after };
    case 'INNER': {
      const w = peekWord(source, after, end);
      return w && w.upper === 'JOIN' ? { kind: 'INNER', endIndex: w.end } : null;
    }
    case 'LEFT':
    case 'RIGHT':
    case 'FULL': {
      let pos = after;
      let w = peekWord(source, pos, end);
      if (w && w.upper === 'OUTER') { pos = w.end; w = peekWord(source, pos, end); }
      return w && w.upper === 'JOIN' ? { kind: firstWord, endIndex: w.end } : null;
    }
    case 'CROSS': {
      const w = peekWord(source, after, end);
      if (!w) return null;
      if (w.upper === 'JOIN') return { kind: 'CROSS', endIndex: w.end };
      if (w.upper === 'APPLY') return { kind: 'CROSS_APPLY', endIndex: w.end };
      return null;
    }
    case 'OUTER': {
      const w = peekWord(source, after, end);
      return w && w.upper === 'APPLY' ? { kind: 'OUTER_APPLY', endIndex: w.end } : null;
    }
    case 'NATURAL': {
      const w = peekWord(source, after, end);
      if (w && (w.upper === 'INNER' || w.upper === 'LEFT' || w.upper === 'RIGHT' || w.upper === 'FULL')) {
        const inner = matchJoinRun(w.upper, source, w.end, end, ctx);
        return inner ? { kind: inner.kind, endIndex: inner.endIndex } : null;
      }
      return w && w.upper === 'JOIN' ? { kind: 'INNER', endIndex: w.end } : null;
    }
    default:
      return null;
  }
}

function peekWord(source: string, from: number, end: number): PeekedWord | null {
  let i = from;
  while (i < end && /\s/.test(source[i])) i++;
  if (i >= end || !IDENT_START.test(source[i])) return null;
  const start = i;
  i++;
  while (i < end && IDENT_CONT.test(source[i])) i++;
  return { upper: source.slice(start, i).toUpperCase(), start, end: i };
}

interface InnerScan {
  readonly rhsEnd: number;
  readonly on: JoinSpan | null;
  readonly using: JoinSpan | null;
}

function scanInnerStrip(
  source: string,
  start: number,
  end: number,
  ctx: SkipContext,
): InnerScan {
  let i = start;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren > 0) { i++; continue; }
    if (/\s/.test(c)) { i++; continue; }
    if (!IDENT_START.test(c)) { i++; continue; }
    const wStart = i;
    i++;
    while (i < end && IDENT_CONT.test(source[i])) i++;
    const word = source.slice(wStart, i).toUpperCase();
    if (word === 'ON') {
      return { rhsEnd: wStart, on: makeSpan(source, i, end), using: null };
    }
    if (word === 'USING') {
      const groupStart = skipWhitespace(source, i, end);
      if (groupStart < end && source[groupStart] === '(') {
        const groupEnd = consumeParenGroup(source, groupStart, end, ctx);
        return { rhsEnd: wStart, on: null, using: makeSpan(source, groupStart, groupEnd) };
      }
      return { rhsEnd: wStart, on: null, using: null };
    }
  }
  return { rhsEnd: end, on: null, using: null };
}

function skipWhitespace(source: string, from: number, end: number): number {
  let i = from;
  while (i < end && /\s/.test(source[i])) i++;
  return i;
}

function makeSpan(source: string, start: number, end: number): JoinSpan {
  let s = start;
  while (s < end && /\s/.test(source[s])) s++;
  let e = end;
  while (e > s && /\s/.test(source[e - 1])) e--;
  return { start: s, end: e, text: source.slice(s, e) };
}
